// ===========================================
// DevPad Landing Page - Modern Bento Layout
// ===========================================
// 3 sections: Hero (WHY) → Bento Grid (WHAT) → CTA (HOW)
// Visual-first, minimal text, clear value

import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  FolderKanban,
  DollarSign,
  SquareCode,
  BookOpen,
  ArrowRight,
  Zap,
  X,
  Check,
  Bot
} from 'lucide-react';
import './Landing.scss';

// ===========================================
// Color themes - restore user preference on unmount
// ===========================================
const COLOR_THEMES = [
  { name: 'Amber', value: '#f59e0b', hover: '#fbbf24' },
  { name: 'Green', value: '#22c55e', hover: '#4ade80' },
  { name: 'Blue', value: '#3b82f6', hover: '#60a5fa' },
  { name: 'Purple', value: '#a855f7', hover: '#c084fc' },
  { name: 'Red', value: '#ef4444', hover: '#f87171' },
  { name: 'Teal', value: '#14b8a6', hover: '#2dd4bf' },
  { name: 'Pink', value: '#ec4899', hover: '#f472b6' },
];

const Landing = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [originalTheme, setOriginalTheme] = useState(null);

  // ===========================================
  // Store original theme on mount, restore on unmount
  // ===========================================
  useLayoutEffect(() => {
    const stored = localStorage.getItem('devpad-theme');
    if (stored) {
      try {
        setOriginalTheme(JSON.parse(stored));
      } catch (e) {
        setOriginalTheme(COLOR_THEMES[0]);
      }
    }
    // Set landing page theme (amber)
    document.documentElement.style.setProperty('--accent-color', '#f59e0b');
    document.documentElement.style.setProperty('--accent-hover', '#fbbf24');

    return () => {
      // Restore user's theme when leaving
      if (originalTheme) {
        document.documentElement.style.setProperty('--accent-color', originalTheme.value);
        document.documentElement.style.setProperty('--accent-hover', originalTheme.hover);
      }
    };
  }, [originalTheme]);

  // ===========================================
  // Redirect authenticated users
  // ===========================================
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/profile');
    }
  }, [isAuthenticated, authLoading, navigate]);

  if (authLoading) {
    return <div className="landing-loading">Loading...</div>;
  }

  // ===========================================
  // Bento grid items - problem → solution pairs
  // ===========================================
  const pillars = [
    {
      id: 'projects',
      icon: FolderKanban,
      name: 'Projects',
      without: 'Scattered across Notion, docs, and your head',
      with: 'Roadmaps, stacks, services, env vars — one place',
      color: '#22c55e',
      size: 'large' // Hero card
    },
    {
      id: 'prompts',
      icon: SquareCode,
      name: 'Prompts',
      without: 'Lost in chat history',
      with: 'Saved, tagged, versioned',
      color: '#a855f7',
      size: 'small'
    },
    {
      id: 'notes',
      icon: BookOpen,
      name: 'Notes',
      without: 'Written and forgotten',
      with: 'Searchable by meaning',
      color: '#3b82f6',
      size: 'small'
    },
    {
      id: 'costs',
      icon: DollarSign,
      name: 'Costs',
      without: 'No idea what you\'re spending',
      with: 'Tracked across every project',
      color: '#14b8a6',
      size: 'wide' // Spans bottom
    }
  ];

  return (
    <div className="landing">
      {/* Ambient background */}
      <div className="landing__bg">
        <div className="landing__glow landing__glow--1" />
        <div className="landing__glow landing__glow--2" />
        <div className="landing__noise" />
      </div>

      {/* ===========================================
          Header
          =========================================== */}
      <header className="landing__header">
        <div className="landing__logo">
          <Zap className="landing__logo-icon" size={24} />
          <span className="landing__logo-text">DevPad</span>
        </div>
        <nav className="landing__nav">
          <Link to="/login" className="landing__nav-link">Sign In</Link>
          <Link to="/register" className="landing__nav-cta">Get Started</Link>
        </nav>
      </header>

      {/* ===========================================
          Hero - The WHY
          =========================================== */}
      <section className="landing__hero">
        <h1 className="landing__title">
          Your work should compound,
          <span className="landing__title-gradient"> not disappear</span>
        </h1>
        <div className="landing__problems">
          <div className="landing__problem-line">
            <X size={16} />
            <span>Projects get stale.</span>
          </div>
          <div className="landing__problem-line">
            <X size={16} />
            <span>Prompts get rewritten.</span>
          </div>
          <div className="landing__problem-line">
            <X size={16} />
            <span>Notes get lost.</span>
          </div>
          <div className="landing__solution-line">
            <Check size={16} />
            <span>DevPad keeps your context intact.</span>
          </div>
        </div>
        <div className="landing__hero-cta">
          <Link to="/register" className="landing__cta-primary">
            Start Building
            <ArrowRight size={18} />
          </Link>
          <Link to="/login" className="landing__cta-secondary">
            Sign In
          </Link>
        </div>
        <blockquote className="landing__quote">
          <p>
            I built DevPad because I had too many projects without a simple hq to manage it all. Every prompt I wrote disappeared into chat history. Every note lived in a different app. Every AI conversation started from zero with no context. Now there's one place where my projects, prompts, and notes become searchable driving context that compounds. With an awesome community contributing notes and prompts.
          </p>
          <cite>— Austin, Founder</cite>
        </blockquote>
      </section>

      {/* ===========================================
          Bento Grid - The WHAT
          =========================================== */}
      <section className="landing__bento">
        <div className="landing__bento-grid">
          {/* Projects - Large hero card */}
          <div className="landing__bento-card landing__bento-card--large" style={{ '--card-color': pillars[0].color }}>
            <div className="landing__bento-header">
              <FolderKanban size={20} />
              <span>Projects</span>
            </div>
            <div className="landing__bento-transform">
              <div className="landing__bento-before">
                <X size={14} />
                <span>Scattered across Notion, docs, and your head</span>
              </div>
              <div className="landing__bento-after">
                <Check size={14} />
                <span>Roadmaps, stacks, services, env vars — one place</span>
              </div>
            </div>
            {/* Mini visual hint */}
            <div className="landing__bento-visual landing__bento-visual--projects">
              <div className="landing__mini-card">
                <span className="landing__mini-status" />
                Billing API
              </div>
              <div className="landing__mini-card">
                <span className="landing__mini-status landing__mini-status--active" />
                DevPad
              </div>
              <div className="landing__mini-card">
                <span className="landing__mini-status landing__mini-status--paused" />
                Side Project
              </div>
            </div>
          </div>

          {/* Prompts - Small card */}
          <div className="landing__bento-card landing__bento-card--small" style={{ '--card-color': pillars[1].color }}>
            <div className="landing__bento-header">
              <SquareCode size={18} />
              <span>Prompts</span>
            </div>
            <div className="landing__bento-transform">
              <div className="landing__bento-before">
                <X size={14} />
                <span>Lost in chat history</span>
              </div>
              <div className="landing__bento-after">
                <Check size={14} />
                <span>Saved, tagged, versioned</span>
              </div>
            </div>
          </div>

          {/* Notes - Small card */}
          <div className="landing__bento-card landing__bento-card--small" style={{ '--card-color': pillars[2].color }}>
            <div className="landing__bento-header">
              <BookOpen size={18} />
              <span>Notes</span>
            </div>
            <div className="landing__bento-transform">
              <div className="landing__bento-before">
                <X size={14} />
                <span>Written and forgotten</span>
              </div>
              <div className="landing__bento-after">
                <Check size={14} />
                <span>Searchable by meaning</span>
              </div>
            </div>
          </div>

          {/* Costs - Wide card */}
          <div className="landing__bento-card landing__bento-card--wide" style={{ '--card-color': pillars[3].color }}>
            <div className="landing__bento-header">
              <DollarSign size={18} />
              <span>Costs</span>
            </div>
            <div className="landing__bento-transform landing__bento-transform--inline">
              <div className="landing__bento-before">
                <X size={14} />
                <span>No idea what you're spending</span>
              </div>
              <div className="landing__bento-after">
                <Check size={14} />
                <span>Tracked across every project</span>
              </div>
            </div>
          </div>

          {/* AI - Accent card */}
          <div className="landing__bento-card landing__bento-card--accent">
            <div className="landing__bento-header">
              <Bot size={18} />
              <span>AI that knows your context</span>
            </div>
            <p className="landing__bento-desc">
              Ask questions. Get answers grounded in what you've actually saved.
            </p>
          </div>
        </div>
      </section>

      {/* ===========================================
          CTA - The HOW
          =========================================== */}
      <section className="landing__final-cta">
        <h2 className="landing__final-title">
          Your future self will thank you
        </h2>
        <p className="landing__final-subtitle">
          Free to start. No credit card.
        </p>
        <Link to="/register" className="landing__final-button">
          Get Started
          <ArrowRight size={18} />
        </Link>
      </section>

      {/* ===========================================
          Footer
          =========================================== */}
      <footer className="landing__footer">
        <div className="landing__footer-content">
          <div className="landing__footer-brand">
            <Zap size={18} />
            <span>DevPad</span>
          </div>
          <div className="landing__footer-links">
            <Link to="/login">Sign In</Link>
            <Link to="/register">Register</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
